import { apiClient } from './api-client';

/**
 * Tenant V3 Email Template Library — Phase 12.5C.
 *
 * The tenant Communication page previously called `GET /email-templates`, which
 * returns only *materialized* tenant rows. A tenant that has never customized a
 * template has none, so the page reported "No templates found" while every
 * platform master was in fact available to it (D-030 read-through).
 *
 * This client speaks to `/email-template-library`, the read-through view that
 * merges the platform masters with whatever the tenant has done to them.
 *
 * NOTE: `apiClient`'s response interceptor already unwraps `{ data, meta }` → `T`,
 * so `r.data` is the payload. Never double-unwrap.
 */

// ── Types (mirror the backend DTOs exactly) ──────────────────────────────────

export type AdoptionStatus =
  | 'USING_PLATFORM_DEFAULT'
  | 'CUSTOMIZED'
  | 'PLATFORM_UPDATE_AVAILABLE'
  | 'IGNORED'
  | 'DISABLED'
  | 'DRAFT'
  | 'ARCHIVED';

export interface TemplateAdoption {
  templateKey: string;
  name: string;
  category: string;
  channelType: string;
  masterTemplateId: string | null;
  /** null until the tenant has interacted — the row is materialized lazily. */
  tenantTemplateId: string | null;
  isOverride: boolean;
  status: string;
  adoptionStatus: AdoptionStatus;
  platformVersion: number | null;
  tenantVersion: number | null;
  updateAvailable: boolean;
  syncedPlatformVersion: number | null;
  dismissedPlatformVersion: number | null;
  platformReleaseDate: string | null;
  tenantUpdatedDate: string | null;
  customizedBy: string | null;
  isArchived: boolean;

  // Draft lifecycle (OD-1)
  hasUnpublishedChanges: boolean;
  draftSubject: string | null;
  draftHtmlBody: string | null;
  draftPlainTextBody: string | null;

  /** Live content actually served today. */
  subject: string;
  htmlBody: string;
  plainTextBody: string;

  assignedGatewayId: string | null;
}

export interface VariableDelta {
  added: string[];
  removed: string[];
  unchanged: string[];
}

export interface TemplateCompare {
  templateKey: string;
  tenant: { subject: string; htmlBody: string; plainTextBody: string; version: number };
  platform: { subject: string; htmlBody: string; plainTextBody: string; version: number };
  changed: { subject: boolean; htmlBody: boolean; plainTextBody: boolean };
  variables: VariableDelta;
}

export interface ReleaseNotes {
  summary?: string;
  featuresAdded?: string[];
  featuresRemoved?: string[];
  variablesAdded?: string[];
  variablesRemoved?: string[];
  breakingChanges?: string;
}

export interface TemplateUpdatePreview {
  templateKey: string;
  currentVersion: number;
  platformVersion: number;
  updateAvailable: boolean;
  releaseNotes: ReleaseNotes | null;
  releaseDate: string | null;
  /**
   * The platform actor. Today this is a UUID, not a name — platform-user name
   * resolution is deferred to Phase D (COMMUNICATION_DESIGN §4.4/§9). The UI
   * must not render it raw.
   */
  releasedBy: string | null;
  variables: VariableDelta;
  breakingChanges: string | null;
}

export type HistoryEntryType =
  | 'PLATFORM_INITIAL'
  | 'CUSTOMIZED'
  | 'UPDATED_FROM_PLATFORM'
  | 'RESET_TO_PLATFORM'
  | 'PLATFORM_PUBLISHED';

export interface TemplateHistoryEntry {
  type: HistoryEntryType;
  versionNumber: number;
  subject: string;
  changeNote: string | null;
  actor: string | null;
  occurredAt: string;
  releaseNotes?: ReleaseNotes | null;
}

export interface SendTestResult {
  success: boolean;
  recipient: string;
  resolvedFrom: 'TENANT' | 'MASTER';
  message: string;
}

/**
 * Draft save payload.
 *
 * `assignedGatewayId` is deliberately part of the same call (OD-2) but is NOT
 * draft state — the backend applies it immediately, because it is routing
 * configuration rather than content and has no draft column.
 */
export interface SaveDraftInput {
  subject?: string;
  htmlBody?: string;
  plainTextBody?: string;
  name?: string;
  assignedGatewayId?: string | null;
}

const BASE = '/email-template-library';

// ── Reads ────────────────────────────────────────────────────────────────────

export interface LibraryParams {
  category?: string;
  search?: string;
}

async function list(params: LibraryParams = {}): Promise<TemplateAdoption[]> {
  const query: Record<string, string> = {};
  if (params.category) query.category = params.category;
  if (params.search) query.search = params.search;
  const r = await apiClient.get(BASE, { params: query });
  return Array.isArray(r.data) ? (r.data as TemplateAdoption[]) : [];
}

async function updates(): Promise<TemplateAdoption[]> {
  const r = await apiClient.get(`${BASE}/updates`);
  return Array.isArray(r.data) ? (r.data as TemplateAdoption[]) : [];
}

async function adoption(key: string): Promise<TemplateAdoption> {
  const r = await apiClient.get(`${BASE}/${encodeURIComponent(key)}/adoption`);
  return r.data as TemplateAdoption;
}

async function compare(key: string): Promise<TemplateCompare> {
  const r = await apiClient.get(`${BASE}/${encodeURIComponent(key)}/compare`);
  return r.data as TemplateCompare;
}

async function updatePreview(key: string): Promise<TemplateUpdatePreview> {
  const r = await apiClient.get(`${BASE}/${encodeURIComponent(key)}/update-preview`);
  return r.data as TemplateUpdatePreview;
}

async function history(key: string): Promise<TemplateHistoryEntry[]> {
  const r = await apiClient.get(`${BASE}/${encodeURIComponent(key)}/history`);
  return Array.isArray(r.data) ? (r.data as TemplateHistoryEntry[]) : [];
}

// ── Writes ───────────────────────────────────────────────────────────────────

const post = async (key: string, action: string, body?: unknown): Promise<TemplateAdoption> => {
  const r = await apiClient.post(`${BASE}/${encodeURIComponent(key)}/${action}`, body ?? {});
  return r.data as TemplateAdoption;
};

/** Saves the DRAFT. Never changes live email content — publish does that. */
const saveDraft = (key: string, input: SaveDraftInput) => post(key, 'customize', input);

/** Promotes the draft to live. This is the only call that changes what recipients receive. */
const publishDraft = (key: string) => post(key, 'publish');

/** Clears the draft. Live content is untouched. */
const discardDraft = (key: string) => post(key, 'discard-draft');

const enable = (key: string) => post(key, 'enable');
const disable = (key: string) => post(key, 'disable');
const updateFromPlatform = (key: string) => post(key, 'update-from-platform');
const resetToPlatform = (key: string) => post(key, 'reset-to-platform');
const ignoreVersion = (key: string) => post(key, 'ignore-version');

async function sendTest(
  key: string,
  recipient: string,
  context?: Record<string, string>,
): Promise<SendTestResult> {
  const body: Record<string, unknown> = { recipient };
  if (context) body.context = context;
  const r = await apiClient.post(`${BASE}/${encodeURIComponent(key)}/send-test`, body);
  return r.data as SendTestResult;
}

export const emailTemplateLibraryService = {
  list,
  updates,
  adoption,
  compare,
  updatePreview,
  history,
  saveDraft,
  publishDraft,
  discardDraft,
  enable,
  disable,
  updateFromPlatform,
  resetToPlatform,
  ignoreVersion,
  sendTest,
};
